import { ModelType } from '../types/models';
import { sendMessage } from './providers/groq/client';
import { MODELS_DATABASE, ModelInfo } from '../config/modelsDatabase';

export interface Capability {
  type: 'writing' | 'coding' | 'reasoning' | 'math' | 'speed' | 'common sense' | 'multilingual';
}

export interface ModelRecommendation {
  name: string;
  provider: string;
  reason: string;
  score: number;
}

export interface RouterResult {
  recommendedModel: string;  // For backward compatibility
  confidence: number;
  reasoning: string;
  idealModel?: {  // For backward compatibility
    name: string;
    provider: string;
    reason: string;
  };
  detectedCapability: Capability['type'];
  performanceModel: {
    name: string;
    reason: string;
    score?: number;
  };
  costEfficiencyModel: {
    name: string;
    reason: string;
    score?: number;
  };
  alternativeModels?: ModelRecommendation[];
}

// User preference weight configuration
// These values can be adjusted to change how each factor is weighted in the model selection
const DEFAULT_WEIGHTS = {
  performance: 0.7, // How much to prioritize performance over cost
  speed: 0.5,      // How important speed is for the recommendation
  cost: 0.3,       // How important cost efficiency is
};

// Min/max thresholds for model filtering
const THRESHOLDS = {
  minPerformance: 0.60, // Minimum acceptable performance score
  maxCost: 0.8,        // Maximum acceptable cost score
};

const getLanguage = () => localStorage.getItem('language') || 'en';

const getLocalizedReason = (en: string, es: string) => {
  const language = localStorage.getItem('language') || 'en';
  return language === 'es' ? es : en;
};

// Map capability types to score properties in the model database
const capabilityToScores = {
  'writing': 'writing',
  'coding': 'coding',
  'reasoning': 'reasoning',
  'math': 'math',
  'speed': 'speed',
  'common sense': 'reasoning',
  'multilingual': 'writing',
} as const;

// Calculate a weighted score for model selection based on the detected capability
function calculateModelScore(
  model: ModelInfo, 
  capability: Capability['type'],
  weights = DEFAULT_WEIGHTS
): number {
  // Get the primary score for this capability
  const primaryMetric = capabilityToScores[capability] as keyof ModelInfo['scores'];
  
  // Calculate a weighted score
  const performanceScore = model.scores[primaryMetric] || 0;
  const speedScore = model.scores.speed || 0;
  const costScore = 1 - model.scores.cost; // Invert cost so higher is better (more affordable)

  return (
    (performanceScore * weights.performance) +
    (speedScore * weights.speed) +
    (costScore * weights.cost)
  ) / (weights.performance + weights.speed + weights.cost); // Normalize to 0-1 range
}

// Helper function to find best models for a capability using scoring
function findBestModels(capability: Capability['type']): {
  performanceModel: { name: string; reason: string; score: number };
  costEfficiencyModel: { name: string; reason: string; score: number };
  alternativeModels: ModelRecommendation[];
} {
  const models = Object.values(MODELS_DATABASE);
  
  // Calculate scores for all models for this capability
  const scoredModels = models.map(model => {
    // Calculate the base score based on capability
    const primaryMetric = capabilityToScores[capability] as keyof ModelInfo['scores'];
    const baseScore = model.scores[primaryMetric] || 0;
    
    // Calculate a performance-oriented score
    const performanceScore = calculateModelScore(model, capability, {
      performance: 0.8,
      speed: 0.1,
      cost: 0.1
    });
    
    // Calculate a cost-efficiency-oriented score
    const costEfficiencyScore = calculateModelScore(model, capability, {
      performance: 0.4,
      speed: 0.3,
      cost: 0.7
    });
    
    return {
      model,
      baseScore,
      performanceScore,
      costEfficiencyScore,
    };
  });
  
  // Filter out models that don't meet minimum criteria
  const filteredModels = scoredModels.filter(({ model, baseScore }) => {
    const primaryMetric = capabilityToScores[capability] as keyof ModelInfo['scores'];
    return (
      model.scores[primaryMetric] >= THRESHOLDS.minPerformance && 
      model.scores.cost <= THRESHOLDS.maxCost
    );
  });
  
  // Sort by performance and cost-efficiency scores
  const sortedByPerformance = [...filteredModels].sort((a, b) => b.performanceScore - a.performanceScore);
  const sortedByCostEfficiency = [...filteredModels].sort((a, b) => b.costEfficiencyScore - a.costEfficiencyScore);
  
  // Get top models in each category
  const topPerformanceModel = sortedByPerformance[0]?.model || models.find(m => m.type.includes('Expert'));
  const topCostEfficiencyModel = sortedByCostEfficiency[0]?.model || models.find(m => m.type.includes('Basic'));
  
  // Get top alternative models (excluding the top performance and cost models)
  const alternativeModels = sortedByPerformance
    .slice(1, 4) // Get the next 3 best models
    .filter(({ model }) => 
      model.name !== topPerformanceModel?.name && 
      model.name !== topCostEfficiencyModel?.name
    )
    .map(({ model, performanceScore }) => ({
      name: model.name,
      provider: model.provider,
      reason: getLocalizedReason(
        `Alternative option for ${capability} tasks (${Math.round(performanceScore * 100)}% match)`,
        `Opción alternativa para tareas de ${capability} (${Math.round(performanceScore * 100)}% coincidencia)`
      ),
      score: performanceScore
    }));
  
  // Build detailed reasons incorporating scores
  const performanceReason = getLocalizedReason(
    `Best model for ${capability} tasks (${Math.round((topPerformanceModel?.scores[capabilityToScores[capability]] || 0) * 100)}% capability score)`,
    `Mejor modelo para tareas de ${capability} (${Math.round((topPerformanceModel?.scores[capabilityToScores[capability]] || 0) * 100)}% puntuación de capacidad)`
  );
  
  const costEfficiencyReason = getLocalizedReason(
    `Cost-effective model for ${capability} tasks (${Math.round((topCostEfficiencyModel?.scores[capabilityToScores[capability]] || 0) * 100)}% capability, ${Math.round((1 - (topCostEfficiencyModel?.scores.cost || 0)) * 100)}% cost efficiency)`,
    `Modelo rentable para tareas de ${capability} (${Math.round((topCostEfficiencyModel?.scores[capabilityToScores[capability]] || 0) * 100)}% capacidad, ${Math.round((1 - (topCostEfficiencyModel?.scores.cost || 0)) * 100)}% eficiencia de coste)`
  );

  return {
    performanceModel: {
      name: topPerformanceModel?.name || 'GPT-4',
      reason: performanceReason,
      score: sortedByPerformance[0]?.performanceScore || 0.9
    },
    costEfficiencyModel: {
      name: topCostEfficiencyModel?.name || 'GPT-3.5 Turbo',
      reason: costEfficiencyReason,
      score: sortedByCostEfficiency[0]?.costEfficiencyScore || 0.8
    },
    alternativeModels
  };
}

const getSystemPrompt = () => {
  const language = getLanguage();

  if (language === 'es') {
    return `Eres un analizador de capacidades que determina qué capacidad principal se necesita para una tarea.
    Analiza el prompt del usuario y determina cuál de estas capacidades es la más relevante:
    - writing: Para tareas que requieren escritura de alta calidad, como redacción de ensayos, artículos, historias, etc.
       Ejemplo: "Escribe un poema sobre la naturaleza" -> writing
    - coding: Para tareas de programación, como escribir código, depurar, explicar conceptos de programación, etc.
       Ejemplo: "Ayúdame a depurar este código Python" -> coding
    - reasoning: Para tareas que requieren análisis lógico, como resolver problemas complejos, tomar decisiones, etc.
       Ejemplo: "Explica por qué el cielo es azul" -> reasoning
    - math: Para cálculos matemáticos, resolver ecuaciones, explicar conceptos matemáticos, etc.
       Ejemplo: "Calcula la integral de x^2" -> math
    - speed: Cuando la velocidad de respuesta es crítica, como en chats en tiempo real o aplicaciones interactivas.
       Ejemplo: "Necesito una respuesta rápida para esta pregunta" -> speed
    - common sense: Para preguntas de conocimiento general o situaciones que requieren razonamiento de sentido común.
       Ejemplo: "Qué debo hacer si pierdo mi pasaporte en el extranjero?" -> common sense
    - multilingual: Para traducciones, comprender múltiples idiomas, o tareas que involucran lenguaje natural en diferentes idiomas.
       Ejemplo: "Traduce este texto al francés" -> multilingual
    
    También determina la complejidad de la tarea (simple, media o alta) para ajustar mejor la recomendación.
    
    Debes responder con un objeto JSON que contenga SOLO:
    {
      "capability": "una de las capacidades listadas",
      "complexity": "simple | medium | high",
      "confidence": número entre 0 y 1,
      "reasoning": "explicación breve de por qué elegiste esta capacidad y nivel de complejidad"
    }`;
  }

  return `You are a capability analyzer that determines which primary capability is needed for a task.
Analyze the user's prompt and determine which of these capabilities is the most relevant:
- writing: For tasks that require high-quality writing, such as essays, articles, stories, etc.
   Example: "Write a poem about nature" -> writing
- coding: For programming tasks, such as writing code, debugging, explaining programming concepts, etc.
   Example: "Help me debug this Python code" -> coding
- reasoning: For tasks that require logical analysis, such as solving complex problems, making decisions, etc.
   Example: "Explain why the sky is blue" -> reasoning
- math: For mathematical calculations, solving equations, explaining mathematical concepts, etc.
   Example: "Calculate the integral of x^2" -> math
- speed: When response speed is critical, such as in real-time chats or interactive applications.
   Example: "I need a quick answer to this question" -> speed
- common sense: For general knowledge questions or situations that require common-sense reasoning.
   Example: "What should I do if I lose my passport abroad?" -> common sense
- multilingual: For translations, understanding multiple languages, or tasks involving natural language in different languages.
   Example: "Translate this text into French" -> multilingual

Also determine the complexity of the task (simple, medium, or high) to better adjust the recommendation.

You must respond with a JSON object containing ONLY:
{
  "capability": "one of the listed capabilities",
  "complexity": "simple | medium | high",
  "confidence": number between 0 and 1,
  "reasoning": "brief explanation of why you chose this capability and complexity level"
}`;
};

// Enhanced keywords for fallback with additional context patterns
const capabilityKeywords = {
  writing: ["write", "essay", "poem", "story", "article", "content", "draft", "blog", "summarize"],
  coding: ["code", "programming", "debug", "python", "javascript", "algorithm", "function", "class", "implement"],
  reasoning: ["explain", "why", "analyze", "logic", "reason", "deduce", "evaluate", "compare", "discuss"],
  math: ["calculate", "math", "equation", "integral", "derivative", "algebra", "compute", "solve"],
  speed: ["fast", "quick", "urgent", "speed", "immediate", "hurry", "asap", "now"],
  "common sense": ["help", "what should", "general", "knowledge", "common sense", "advice", "recommend", "suggestion"],
  multilingual: ["translate", "spanish", "french", "language", "multilingual", "english", "german", "italian", "japanese"]
};

// Enhanced keyword detection with pattern-based weights
function detectCapabilityFromText(prompt: string): {
  capability: Capability['type'];
  confidence: number;
} {
  const lowerPrompt = prompt.toLowerCase();
  let bestCapability: Capability['type'] = 'common sense'; // Default
  let bestScore = 0;
  
  // Score each capability based on keyword matches
  for (const [capability, keywords] of Object.entries(capabilityKeywords)) {
    let score = 0;
    
    // Count keyword matches
    for (const keyword of keywords) {
      if (lowerPrompt.includes(keyword)) {
        // Primary match
        score += 1;
        
        // Context patterns give bonus points
        if (capability === 'writing' && 
            (lowerPrompt.includes("creative") || lowerPrompt.includes("well-written") || 
             lowerPrompt.includes("professional") || lowerPrompt.match(/write\s+(an?|the)\s+\w+/))) {
          score += 0.5;
        }
        
        if (capability === 'coding' && 
            (lowerPrompt.includes("function") || lowerPrompt.includes("bug") || 
             lowerPrompt.includes("error") || lowerPrompt.match(/in\s+(javascript|python|java|c\+\+|typescript)/))) {
          score += 0.5;
        }
        
        if (capability === 'math' && 
            (lowerPrompt.includes("problem") || lowerPrompt.includes("formula") || 
             lowerPrompt.includes("calculate") || lowerPrompt.match(/\d+[\+\-\*\/\^]\d+/))) {
          score += 0.5;
        }
      }
    }
    
    // Normalize the score based on keyword count to avoid bias from categories with more keywords
    const normalizedScore = score / keywords.length;
    
    if (normalizedScore > bestScore) {
      bestScore = normalizedScore;
      bestCapability = capability as Capability['type'];
    }
  }
  
  // Calculate confidence - at least 0.6 if we found any matches, otherwise 0.5
  const confidence = bestScore > 0 ? Math.min(0.6 + (bestScore * 0.3), 0.9) : 0.5;
  
  return {
    capability: bestCapability,
    confidence
  };
}

// Adjust weights based on complexity and capability type
function getWeightsForTask(capability: Capability['type'], complexity: string = 'medium'): typeof DEFAULT_WEIGHTS {
  // Start with default weights
  const weights = { ...DEFAULT_WEIGHTS };
  
  // Adjust based on complexity
  if (complexity === 'high') {
    weights.performance = 0.9;
    weights.cost = 0.1;
  } else if (complexity === 'simple') {
    weights.performance = 0.5;
    weights.cost = 0.5;
  }
  
  // Adjust based on capability
  if (capability === 'speed') {
    weights.speed = 0.9;
    weights.performance = 0.5;
  } else if (capability === 'coding' || capability === 'math') {
    weights.performance = Math.min(weights.performance + 0.1, 1.0);
  }
  
  return weights;
}

export async function analyzePromptWithGroq(
  prompt: string,
  config: { apiKey: string }
): Promise<RouterResult> {
  try {
    // Input validation and sanitization
    if (!prompt || typeof prompt !== 'string') {
      throw new Error('Invalid prompt provided');
    }
    
    // Limit prompt length to prevent excessive API usage
    const sanitizedPrompt = prompt.slice(0, 1000);
    
    // Escape special characters to prevent injection
    const escapedPrompt = sanitizedPrompt
      .replace(/"/g, '\\"')
      .replace(/\\/g, '\\\\');
    
    const userPrompt = `Analyze this prompt: "${escapedPrompt}"`;
    
    // Validate API key format before use (simple check)
    if (!config.apiKey || typeof config.apiKey !== 'string' || config.apiKey.length < 10) {
      throw new Error('Invalid API key configuration');
    }
    
    // Sanitize the prompt to avoid potential regex issues
    const safeUserPrompt = userPrompt.replace(/\(\?[<P][^)]+\)/g, '');
    
    const response = await sendMessage(
      safeUserPrompt,
      config.apiKey,
      'llama-3.3-70b-versatile',
      { systemPrompt: getSystemPrompt(), userInfo: {} },
      []
    );

    try {
      // Validate response is a string before parsing
      if (typeof response !== 'string') {
        throw new Error('Invalid response format from API');
      }
      
      // Schema validation for expected response format
      const result = JSON.parse(response);
      
      // Validate required fields exist
      if (!result.capability || !result.confidence) {
        throw new Error('Incomplete response from capability analysis');
      }
      
      // Validate capability is one of the expected types
      const validCapabilities: Capability['type'][] = [
        'writing', 'coding', 'reasoning', 'math', 
        'speed', 'common sense', 'multilingual'
      ];
      
      if (!validCapabilities.includes(result.capability)) {
        throw new Error('Invalid capability type returned');
      }
      
      const capability = result.capability as Capability['type'];
      const complexity = result.complexity || 'medium';
      
      // Adjust weights based on task complexity and type
      const weights = getWeightsForTask(capability, complexity);
      
      // Get model recommendations with adjusted weights
      const recommendation = findBestModels(capability);

      return {
        detectedCapability: capability,
        performanceModel: recommendation.performanceModel,
        costEfficiencyModel: recommendation.costEfficiencyModel,
        alternativeModels: recommendation.alternativeModels,
        recommendedModel: recommendation.costEfficiencyModel.name,
        confidence: Math.min(Math.max(0, Number(result.confidence) || 0.7), 1), // Ensure valid confidence range
        reasoning: result.reasoning ? String(result.reasoning).slice(0, 200) : '', // Limit reasoning length
        idealModel: {
          name: recommendation.performanceModel.name,
          provider: MODELS_DATABASE[recommendation.performanceModel.name]?.provider || 'openai',
          reason: recommendation.performanceModel.reason
        }
      };
    } catch (parseError) {
      // Don't log the entire response which might contain sensitive data
      console.error('Error parsing response from capability analysis:', parseError instanceof Error ? parseError.message : 'Unknown error');
      
      // Enhanced fallback: Analyze keywords in the prompt
      const detection = detectCapabilityFromText(sanitizedPrompt);
      const capability = detection.capability;
      
      // Use medium complexity as default for fallback
      const weights = getWeightsForTask(capability, 'medium');
      const recommendation = findBestModels(capability);
      
      return {
        detectedCapability: capability,
        performanceModel: recommendation.performanceModel,
        costEfficiencyModel: recommendation.costEfficiencyModel,
        alternativeModels: recommendation.alternativeModels,
        recommendedModel: recommendation.costEfficiencyModel.name,
        confidence: detection.confidence,
        reasoning: getLocalizedReason(
          `Detected ${capability} capability based on content analysis (${Math.round(detection.confidence * 100)}% confidence)`,
          `Capacidad ${capability} detectada basada en análisis de contenido (${Math.round(detection.confidence * 100)}% confianza)`
        ),
        idealModel: {
          name: recommendation.performanceModel.name,
          provider: MODELS_DATABASE[recommendation.performanceModel.name]?.provider || 'openai',
          reason: recommendation.performanceModel.reason
        }
      };
    }
  } catch (error) {
    // Sanitize error objects to prevent leaking implementation details
    const errorMessage = error instanceof Error ? 
      'Error in capability analysis: ' + error.message.replace(/key|api|token|secret/gi, '[REDACTED]') : 
      'Unknown error in analysis';
    
    console.error(errorMessage);
    
    // Return a safe default rather than exposing error
    const defaultCapability: Capability['type'] = 'common sense';
    const recommendation = findBestModels(defaultCapability);
    
    return {
      detectedCapability: defaultCapability,
      performanceModel: recommendation.performanceModel,
      costEfficiencyModel: recommendation.costEfficiencyModel,
      alternativeModels: recommendation.alternativeModels,
      recommendedModel: recommendation.costEfficiencyModel.name,
      confidence: 0.5,
      reasoning: getLocalizedReason(
        'Error during analysis, using default recommendation',
        'Error durante el análisis, usando recomendación predeterminada'
      ),
      idealModel: {
        name: recommendation.performanceModel.name,
        provider: MODELS_DATABASE[recommendation.performanceModel.name]?.provider || 'openai',
        reason: recommendation.performanceModel.reason
      }
    };
  }
}

export function selectModel(
  result: RouterResult,
  availableModels: string[]
): RouterResult {
  if (!availableModels.length) {
    return result;
  }
  
  // Convert all availableModels to lowercase for case-insensitive matching
  const lowerCaseModelMap = availableModels.reduce((acc, modelId) => {
    acc[modelId.toLowerCase()] = modelId;
    return acc;
  }, {} as Record<string, string>);
  
  // First try performance model
  let bestMatch = availableModels.find(modelId => 
    modelId.toLowerCase().includes(result.performanceModel.name.toLowerCase())
  );
  
  // Then try cost efficiency model
  if (!bestMatch) {
    bestMatch = availableModels.find(modelId => 
      modelId.toLowerCase().includes(result.costEfficiencyModel.name.toLowerCase())
    );
  }
  
  // Then try alternative models
  if (!bestMatch && result.alternativeModels?.length) {
    for (const altModel of result.alternativeModels) {
      const match = availableModels.find(modelId => 
        modelId.toLowerCase().includes(altModel.name.toLowerCase())
      );
      if (match) {
        bestMatch = match;
        break;
      }
    }
  }
  
  // Fallback to first available
  if (!bestMatch && availableModels.length > 0) {
    bestMatch = availableModels[0];
  }

  return {
    ...result,
    recommendedModel: bestMatch || result.recommendedModel
  };
}

export function logRouterDecision(
  prompt: string,
  result: RouterResult
) {
  if (process.env.ROUTER_LOG_ENABLED === 'true') {
    if (process.env.ROUTER_LOG_LEVEL === 'debug' || process.env.ROUTER_LOG_LEVEL === 'info') {
      console.info('LLM Router Decision:', {
        timestamp: new Date().toISOString(),
        prompt,
        capability: result.detectedCapability,
        performanceModel: result.performanceModel,
        costEfficiencyModel: result.costEfficiencyModel,
        alternativeModels: result.alternativeModels,
        confidence: result.confidence,
        recommendedModel: result.recommendedModel,
        idealModel: result.idealModel
      });
    }
  }
}